import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Award, HeartHandshake, TrendingUp, Repeat, Lock, Layers } from "lucide-react";

const forClient = [
  {
    icon: Award,
    title: "Autoridad",
    body:
      "Tu cliente deja de ser \"otro proveedor más\" y se convierte en la voz que explica su sector. Quien enseña, lidera.",
  },
  {
    icon: HeartHandshake,
    title: "Confianza",
    body:
      "Ver y oír a la persona que está detrás de la empresa acorta el ciclo de venta. Llegan a la reunión ya convencidos.",
  },
  {
    icon: TrendingUp,
    title: "Más oportunidades",
    body:
      "Cada episodio es una excusa perfecta para invitar a un cliente potencial, a un partner o a un referente del sector.",
  },
];

const forAgency = [
  {
    icon: Repeat,
    title: "Ingreso recurrente",
    body:
      "El videopodcast no es un proyecto de una vez: es una suscripción mensual de producción y contenido.",
  },
  {
    icon: Layers,
    title: "Alimenta todo lo demás",
    body:
      "De una grabación salen clips verticales, posts, newsletter y anuncios. Tu equipo deja de inventar contenido de cero.",
  },
  {
    icon: Lock,
    title: "Retienes la cuenta",
    body:
      "Un cliente con un podcast en marcha y una audiencia creciendo no cambia de agencia a la ligera.",
  },
];

function Card({ item, delay, isInView, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-shadow"
    >
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-4`}>
        <item.icon className="w-5 h-5 text-white" />
      </div>
      <h4 className="font-heading font-bold text-lg text-galvani-text mb-2">{item.title}</h4>
      <p className="font-body text-sm text-galvani-textLight leading-relaxed">{item.body}</p>
    </motion.div>
  );
}

export default function AgenciesOpportunity() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="oportunidad"
      className="section-spacing bg-white relative overflow-hidden"
      data-testid="agencies-opportunity"
    >
      <div className="absolute -top-32 right-0 w-96 h-96 bg-purple-200/25 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-12"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-pink font-body font-semibold mb-4">
            La oportunidad
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-5">
            Hoy no vende el que más grita.{" "}
            <span className="gradient-text">Vende el que más confianza genera</span>
          </h2>
          <p className="font-body text-base text-galvani-textLight leading-relaxed">
            Tu cliente puede tener el mejor producto de su sector y seguir perdiendo operaciones
            frente a un competidor peor pero más conocido. La diferencia ya no está en el precio ni
            en el anuncio: está en quién se ha ganado la credibilidad del mercado antes de que
            llegue el momento de comprar. Eso es exactamente lo que construye un videopodcast, y es
            uno de los pocos servicios que una agencia puede vender como generador de{" "}
            <strong className="text-galvani-text">autoridad</strong> y no como otro entregable más.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mb-16 grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {[
            {
              stat: "Antes de la reunión",
              text: "El comprador B2B se informa solo antes de hablar con nadie. Si en esa búsqueda encuentra a tu cliente explicando su sector con criterio, la conversación empieza ganada.",
            },
            {
              stat: "Cara y voz",
              text: "Nada genera más confianza que ver y escuchar a la persona que dirige la empresa. Ni un PDF, ni una landing, ni un carrusel.",
            },
            {
              stat: "Semana a semana",
              text: "La autoridad no se compra en una campaña: se construye publicando con constancia. Por eso es un servicio recurrente y no un proyecto puntual.",
            },
          ].map((b) => (
            <div
              key={b.stat}
              className="p-6 rounded-2xl bg-galvani-bg border-l-4 border-galvani-purple"
            >
              <p className="font-heading font-bold text-sm uppercase tracking-wider text-galvani-purple mb-2">
                {b.stat}
              </p>
              <p className="font-body text-sm text-galvani-text leading-relaxed">{b.text}</p>
            </div>
          ))}
        </motion.div>

        <div className="mb-16">
          <h3 className="font-heading font-bold text-xl text-galvani-text mb-6">
            Lo que gana <span className="text-galvani-purple">tu cliente</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {forClient.map((item, i) => (
              <Card
                key={item.title}
                item={item}
                delay={0.1 + i * 0.08}
                isInView={isInView}
                accent="from-galvani-blue to-galvani-purple"
              />
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-heading font-bold text-xl text-galvani-text mb-6">
            Lo que ganas <span className="text-galvani-purple">tú como agencia</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {forAgency.map((item, i) => (
              <Card
                key={item.title}
                item={item}
                delay={0.3 + i * 0.08}
                isInView={isInView}
                accent="from-galvani-cyan to-galvani-blue"
              />
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-14 p-7 md:p-9 rounded-3xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-100"
        >
          <p className="font-body text-base md:text-lg text-galvani-text leading-relaxed">
            <strong className="font-semibold">La parte más incómoda de vender audiovisual</strong>{" "}
            es la producción: cámaras, sonido, edición, plazos y una persona pendiente de todo.
            Esa parte la asumimos nosotros. Tú te quedas con la estrategia, la relación con el
            cliente y el margen.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
